import React, { PureComponent } from 'react';
import { debounce, noop } from 'lodash';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import reduxUtils from 'util/redux';
import FormField from 'components/form/form-field-input';
import Loader from 'components/loader';
import Button from 'components/button';
import EmailInput from 'components/email-input';
import t, { Trans, TransMD } from 'translations';
import $ from 'bianco.query';
import Icon from 'components/icon';
import Collapsible from 'react-collapsible';
import { getRegionSlugByCooperative } from 'util/misc/get-region';
import { linkToDashboard } from 'util/misc/link-to-dashboard';
import tracking from 'tracking';
import { FORM_TYPE_ZIP, SUBSCRIBE_EVENT, UPDATE_ZIP_EVENT } from 'tracking/constants';

const MAX_ZIP_LENGTH = 4;


class ZipForm extends PureComponent {
    static defaultProps = {
        descriptionPath: '',
        hasAccordion: true,
        onHeightChange: noop,
        autoFocus: false,
        onSuccess: noop,
    };

    static propTypes = {
        descriptionPath: PropTypes.string,
        hasAccordion: PropTypes.bool,
        onHeightChange: PropTypes.func.isRequired,
        isFetching: PropTypes.bool.isRequired,
        subscriptionEmail: PropTypes.string.isRequired,
        isSendingSubscription: PropTypes.bool.isRequired,
        subscriptionError: PropTypes.string,
        history: PropTypes.object.isRequired,
        routeName: PropTypes.string.isRequired,
        routerRegistry: PropTypes.object.isRequired,
        regionActive: PropTypes.object,
        regionRequested: PropTypes.object,
        resetRequestedRegion: PropTypes.func.isRequired,
        subscribe: PropTypes.func.isRequired,
        cartItems: PropTypes.object.isRequired,
        setRegion: PropTypes.func.isRequired,
        onSuccess: PropTypes.func,
        closeModal: PropTypes.func,
        autoFocus: PropTypes.bool,
    };

    /**
     * Get the zip form title depending on the active region
     * @param {object} regionActive - active user region
     * @returns {string}
     */
    static getZipFormTitle(regionActive) {
        if (regionActive) {
            return 'zip.modal.title.update';
        }

        return 'zip.modal.title.change';
    }

    static getSubmitButtonText(regionActive) {
        if (regionActive) {
            return 'zip.form.save';
        }

        return 'zip.form.go';
    }

    constructor(props) {
        super(props);
        const { regionActive } = props;

        this.state = {
            isEmailValid: false,
            isAccordionOpen: false,
            hasZip: !!(regionActive && regionActive.zip),
            zip: '',
        };
    }

    componentDidUpdate() {
        if (this.props.regionRequested && !this.props.regionRequested.enabled) {
            this.props.onHeightChange();
        }
    }

    componentWillUnmount() {
        this.props.resetRequestedRegion();
    }

    onSubmitZip = (e) => {
        const { zip } = this.state;
        const hasRegion = !!this.props.regionActive;
        e.preventDefault();
        if (zip) {
            this.props.setRegion({ zip }).then(({ enabled }) => {
                if (enabled) {
                    this.props.onSuccess();
                }

                tracking.form({
                    zip,
                    event: hasRegion ? UPDATE_ZIP_EVENT : SUBSCRIBE_EVENT,
                    formType: FORM_TYPE_ZIP,
                    formStep: enabled ? 'success' : 'error',
                });
            });
        }
    };

    onSubscribe = (e) => {
        e.preventDefault();

        const { zip } = this.state;

        const [emailInput] = $('input[type="email"]', this.refs.root);
        const value = emailInput.value;
        const isValid = emailInput.checkValidity();

        this.setState({ isEmailValid: isValid });

        if (!value || !isValid) {
            return;
        }

        // this.props.subscribe({ email: value }).then(() => tracking.form({ email: value }));
        this.props.subscribe({ email: value, zip });
    };

    onZipInput = e => {
        if (e.length <= MAX_ZIP_LENGTH) {
            this.setState({ zip: e });
        }
    };

    onEmailChange = ({ isValid }) => {
        this.setState({
            isEmailValid: isValid,
        });
    };

    freeNavigation = () => {
        this.props.closeModal();
    };

    renderAccordion() {
        return (
            <Collapsible onOpen={this.props.onHeightChange}
                onClose={debounce(this.props.onHeightChange, 50)} classParentString="zip-form--accordion"
                transitionTime={5}
                onOpening={() => this.setState({ isAccordionOpen: true })}
                onClosing={() => this.setState({ isAccordionOpen: false })}
                trigger={this.renderTrigger()}>
                <div className="zip-form--accordion-content" aria-hidden={!this.state.isAccordionOpen}>
                    <h2 className="zip-form--accordion-title"><Trans path="zip.accordion.title"/></h2>

                    <p className="zip-form--paragraph"><TransMD path="zip.accordion.text"/></p>
                </div>
            </Collapsible>
        );
    }

    renderEmailForm() {
        const { subscriptionEmail, isSendingSubscription } = this.props;
        const { isEmailValid, zip } = this.state;
        const errorMessages = t('zip.error.invalid_email');

        const isValid = zip && isEmailValid;

        return (
            <div className="zip-form--email">
                {subscriptionEmail ? null : <p className="zip-form--description zip-form--paragraph">
                    <Trans path="zip.error.not_available"/>
                </p>}
                <form noValidate className="zip-form--email-form" onSubmit={this.onSubscribe}>
                    {subscriptionEmail ?
                        <p className="zip-form--success-message zip-form--paragraph"><Trans path="zip.subscribed"/>
                        </p> :
                        <div className="zip-form--row">
                            <div className="zip-form--input-wrapper">
                                <EmailInput
                                    onChange={this.onEmailChange}
                                    messages={{ error: errorMessages }}
                                    label={t('zip.email')}
                                />
                            </div>
                            <div className="zip-form--button-wrapper">
                                <Button size="medium"
                                    disabled={!isValid}
                                    className="zip-form--form-button zip-form--form-button__email"
                                    facet="secondary">
                                    {isSendingSubscription ? <Loader size="small"/> :
                                        <Trans path="zip.send"/>}
                                </Button>
                            </div>
                        </div>}
                </form>
                <p>
                    <Link to={linkToDashboard(getRegionSlugByCooperative())} onClick={this.freeNavigation}><Trans
                        path="zip.keep_browsing"/></Link>
                </p>
            </div>
        );
    }

    renderTrigger() {
        return (
            <div tabIndex="0" role="button" aria-expanded={this.state.isAccordionOpen}
                className="zip-form--accordion-trigger">
                <div className="zip-form--accordion-trigger-text"><Trans path="zip.accordion.trigger"/></div>
                <Icon className="zip-form--accordion-icon" size="small" symbol="chevron-down"/>
            </div>
        );
    }

    render() {
        const {
            descriptionPath,
            hasAccordion,
            regionActive,
            isFetching,
            regionRequested,
            cartItems,
            autoFocus,
        } = this.props;
        const { zip, hasZip } = this.state;
        const submitMessagePath = ZipForm.getSubmitButtonText(regionActive);
        const titlePath = ZipForm.getZipFormTitle(regionActive);
        const hasCartEmpty = !Object.keys(cartItems).length;
        const placeholder = hasZip ? 'zip.form.placeholder_change' : 'zip.form.placeholder';

        return (
            <div ref="root" className="zip-form">
                <div className="zip-form--content">
                    <h2 className="zip-form--title"><Trans path={titlePath}/></h2>
                    {descriptionPath &&
                    <p className="zip-form--description zip-form--paragraph"><Trans path={descriptionPath}/></p>}
                    {hasCartEmpty ? null :
                        <p className="zip-form--description zip-form--paragraph"><TransMD path="zip.clear_cart"/></p>}
                    <form onSubmit={this.onSubmitZip}>
                        <div className="zip-form--row">
                            <div className="zip-form--input-wrapper">
                                <FormField type="number"
                                    value={zip}
                                    state={{ error: regionRequested && !regionRequested.enabled }}
                                    name="zip"
                                    autoFocus={autoFocus}
                                    pattern="[0-9]*"
                                    onInput={this.onZipInput}
                                    hasFloatingLabel={true}
                                    label={t(placeholder)}/>
                            </div>
                            <div className="zip-form--button-wrapper">
                                <Button hasFocus={isFetching}
                                    size="medium"
                                    className="zip-form--form-button zip-form--form-button__zip"
                                    facet="primary">
                                    {isFetching ? <Loader size="small"/> :
                                        <Trans path={submitMessagePath}/>}
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
                {regionRequested && !regionRequested.enabled && this.renderEmailForm()}
                {hasAccordion && this.renderAccordion()}
            </div>
        );
    }
}

export default connect(
    reduxUtils.mapStateToProps({
        regionRequested: 'region.requested',
        regionActive: 'region.active',
        subscriptionEmail: 'subscription.email',
        subscriptionError: 'subscription.error',
        isSendingSubscription: 'subscription.isFetching',
        isFetching: 'region.isFetching',
        cartItems: 'cart.items',
        routerRegistry: 'router.registry',
        history: 'router.history',
        routeName: 'router.name',
        isCartEmpty: 'cart.isEmpty',
    }),
    reduxUtils.mapDispatchToProps({
        resetRequestedRegion: 'region.reset',
        setRegion: 'region.setRegion',
        subscribe: 'subscription.subscribe',
        closeModal: 'modal.close',
    }),
)(ZipForm);



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/zip-form/index.js